import { Autocomplete } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import { AloveDialog, DialogType } from "components/alove/AloveDialog";
import { EditableGridColDef, EditableTable } from "components/alove/EditableTable";
import { setCurrentDialog, useMaterialUIController } from "context";
import DeactivateDialog from "layouts/itemPageContainer/components/deactivateDialog";
import { QuestionResponsesAutomatic } from "models/batch/AutoResponse";
import { Question } from "models/batch/Question";
import { QuestionResponse } from "models/batch/QuestionResponse";
import { useEffect, useState } from "react";
import { tr } from "utils/globalsUtils";
import { createAutoResponse, getAllQuestionsDO, getAutoResponse, executeAutoResponse, getAutoResponses, updateAutoResponse } from "utils/questionsUtils";

const MDInput = require("components/MDInput/index.js").default;

export const Autoresponse = ({
  questionId,
  answers,
}: {
  questionId: number;
  answers: QuestionResponse[];
}) => {
  const [autoResponses, setAutoResponses] = useState<QuestionResponsesAutomatic[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [rowToExecute, setRowToExecute] = useState<QuestionResponsesAutomatic | null>(null);
  const [controller, dispatch] = useMaterialUIController();

  useEffect(() => {
    getAllQuestionsDO(true).then((res) => {
      setQuestions(res.sort((a, b) => a.id - b.id));
    });
  }, []);

  useEffect(() => {
    loadAutoResponses();
  }, [questionId]);

  const loadAutoResponses = () =>
    getAutoResponses(questionId).then((res) => {
      setAutoResponses(res);
    });

  const processRowUpdate = (
    row: QuestionResponsesAutomatic,
    oldRow: QuestionResponsesAutomatic
  ) => {
    updateAutoResponse(questionId, row.id, row).then(() => loadAutoResponses());
    return row;
  };

  const createNewRow = (newRow: QuestionResponsesAutomatic) => {
    return createAutoResponse(questionId, newRow).then((res) => loadAutoResponses());
  };

  const columns: EditableGridColDef[] = [
    {
      field: "answerId",
      headerName: "Answer",
      flex: 1,
      editable: true,
      type: "singleSelect",
      valueGetter: (v, row) => row.answerId?.toString(),
      valueOptions: answers.map((answer) => ({ value: answer.id, label: answer.txt })),
    },
    {
      field: "relayToQuestionId",
      headerName: "Relay To Question",
      flex: 2,
      editable: true,
      customType: "autocomplete",
      type: "custom",
      valueFormatter: (v) => questions.find((q) => q.id == v)?.txt,
      getOptions: () => questions.map((question) => 
        ({ value: question.id, 
          label: question.id + " - " + question.txt + " (" + question.batchContents?.[0]?.batch?.displayName + ")" })),
    },
    {
      field: "relayToReponseId",
      headerName: "Relay To Answer",
      flex: 1,
      editable: true,
      customType: "autocomplete",
      type: "custom",
      valueFormatter: (v, row) =>
        questions.find((q) => q.id == row.relayToQuestionId)?.responses.find((r) => r.id == v)?.txt,
      getOptions: (params) => {
        const question = questions.find((q) => q.id == params.row.relayToQuestionId);
        return (question?.responses || []).map((response) => ({
          value: response.id,
          label: response.txt,
        }));
      },
    },
    { field: "value", headerName: "Value", flex: 1, editable: true },
  ];

  return (
    <>
    <EditableTable
      columns={columns}
      rows={autoResponses}
      modelName="Auto Response"
      processRowUpdate={processRowUpdate}
      onAddRow={createNewRow}
      actions={[{
        label: "Execute",
          onClick: (row) => {
            getAutoResponse(questionId, row.id).then((res) => {
              setRowToExecute(res);
              setCurrentDialog(dispatch, DialogType.ExecuteAutoResponse); 
            });
        },
      }]}
    />
    <AloveDialog  
        dialogType={DialogType.ExecuteAutoResponse}
        dialogTitle={`Warning`}
        dialogBody={`You are about to execute this auto response.
          This will effect everyone who answered the question.

          Everyone who answered:
          Question: ${rowToExecute?.question?.txt}
          Answer: ${rowToExecute?.answer?.txt}

          Will be answered with:
          Question: ${rowToExecute?.relayToQuestion?.txt}
          Answer: ${rowToExecute?.relayToReponse?.txt}
          
          Are you sure you want to continue?`}
        dialogConfirm="Execute"
        error
        handleConfirm={async () => {
          executeAutoResponse(questionId, rowToExecute!.id).then(() => {
            loadAutoResponses();
          });
        }}
      />
    </>
  );
};
